import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

import Swal from "sweetalert2";
import { API_BASE } from "../config/api";

function ChampionsParticipantes() {
    const { registroId } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [detalle, setDetalle] = useState(null);
    const [evidencias, setEvidencias] = useState([]);

    const [activeTab, setActiveTab] = useState("pendientes");

    const [forms, setForms] = useState({});
    const [accionNivel, setAccionNivel] = useState({
        nivelId: "",
        comentario: "",
        enviarCorreo: true,
    });

    const formatDateMexico = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("es-MX", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(date);
};

const formatDateTimeMexico = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("es-MX", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

    const fetchDetalle = async () => {
        const response = await fetch(
            `${API_BASE}/api/admin/champions/participantes/${registroId}`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(
                result.message || "No se pudo consultar el detalle del participante."
            );
        }

        setDetalle(result.data);
    };

    const fetchEvidencias = async () => {
        const response = await fetch(
            `${API_BASE}/api/admin/champions/participantes/${registroId}/evidencias?limit=100`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(
                result.message || "No se pudieron consultar las evidencias."
            );
        }

        const items = result.data?.items || [];
        setEvidencias(items);

        setForms((prev) => {
            const next = { ...prev };

            items.forEach((ev) => {
                if (!next[ev.Comprobante_ID]) {
                    next[ev.Comprobante_ID] = {
                        nivelId: ev.Nivel_ID || "",
                        comentario: ev.Comprobante_Observaciones || "",
                        actualizarProgreso: true,
                        finalizarNivel: false,
                        enviarCorreo: true,
                    };
                }
            });

            return next;
        });
    };

    const fetchAll = async () => {
        try {
            setLoading(true);
            await Promise.all([fetchDetalle(), fetchEvidencias()]);
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, [registroId]);

    const niveles = detalle?.niveles || [];

    const evidenciasPendientes = useMemo(() => {
        return evidencias.filter((ev) =>
            ["PENDIENTE", "EN_REVISION", "PARCIAL"].includes(
                ev.Comprobante_Estatus
            )
        );
    }, [evidencias]);

    const evidenciasRechazadas = useMemo(() => {
        return evidencias.filter(
            (ev) => ev.Comprobante_Estatus === "RECHAZADO"
        );
    }, [evidencias]);

    const evidenciasAprobadasPorNivel = useMemo(() => {
        const map = {};

        niveles.forEach((nivel) => {
            map[nivel.Nivel_ID] = evidencias.filter(
                (ev) =>
                    ev.Comprobante_Estatus === "APROBADO" &&
                    Number(ev.Nivel_ID) === Number(nivel.Nivel_ID)
            );
        });

        return map;
    }, [evidencias, niveles]);

    const getForm = (comprobanteId) => {
        return (
            forms[comprobanteId] || {
                nivelId: "",
                comentario: "",
                actualizarProgreso: true,
                finalizarNivel: false,
                enviarCorreo: true,
            }
        );
    };

    const updateForm = (comprobanteId, field, value) => {
        setForms((prev) => ({
            ...prev,
            [comprobanteId]: {
                ...getForm(comprobanteId),
                [field]: value,
            },
        }));
    };

    const formatEstatusRegistro = (estatus) => {
        const map = {
            INSCRITO: "Inscrito",
            EN_PROGRESO: "En progreso",
            COMPLETADO: "Completado",
            CANCELADO: "Cancelado",
        };

        return map[estatus] || estatus || "Sin estado";
    };

    const formatEstatusEvidencia = (estatus) => {
        const map = {
            PENDIENTE: "Pendiente",
            EN_REVISION: "En revisión",
            APROBADO: "Aprobado",
            RECHAZADO: "Rechazado",
            PARCIAL: "Parcial",
        };

        return map[estatus] || estatus || "Sin estado";
    };

    const getRegistroBadge = (estatus) => {
        switch (estatus) {
            case "INSCRITO":
                return "bg-primary-subtle text-primary";
            case "EN_PROGRESO":
                return "bg-warning-subtle text-warning";
            case "COMPLETADO":
                return "bg-success-subtle text-success";
            case "CANCELADO":
                return "bg-danger-subtle text-danger";
            default:
                return "bg-secondary";
        }
    };

    const getEvidenciaBadge = (estatus) => {
        switch (estatus) {
            case "PENDIENTE":
                return "bg-primary-subtle text-primary";
            case "EN_REVISION":
                return "bg-warning-subtle text-warning";
            case "APROBADO":
                return "bg-success-subtle text-success";
            case "RECHAZADO":
                return "bg-danger-subtle text-danger";
            case "PARCIAL":
                return "bg-secondary-subtle text-secondary";
            default:
                return "bg-light text-dark";
        }
    };

    const getNivelById = (nivelId) => {
        return niveles.find((nivel) => Number(nivel.Nivel_ID) === Number(nivelId));
    };

    const revisarEvidencia = async (evidencia, accion) => {
        const form = getForm(evidencia.Comprobante_ID);

        if (!form.nivelId) {
            Swal.fire({
                icon: "warning",
                title: "Nivel requerido",
                text: "Selecciona el nivel al que se aplicará esta evidencia.",
            });
            return;
        }

        if (accion === "RECHAZAR" && !form.comentario?.trim()) {
            Swal.fire({
                icon: "warning",
                title: "Comentario requerido",
                text: "Para rechazar una evidencia debes dejar un comentario.",
            });
            return;
        }

        const nivelSeleccionado = getNivelById(form.nivelId);

        const confirm = await Swal.fire({
            icon: accion === "APROBAR" ? "question" : "warning",
            title:
                accion === "APROBAR"
                    ? "¿Aprobar evidencia?"
                    : "¿Rechazar evidencia?",
            html: `
                <p class="mb-1">Nivel seleccionado:</p>
                <strong>${nivelSeleccionado?.Nivel_Nombre || "Nivel"}</strong>
            `,
            showCancelButton: true,
            confirmButtonText: accion === "APROBAR" ? "Sí, aprobar" : "Sí, rechazar",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);

            const response = await fetch(
                `${API_BASE}/api/admin/champions/evidencias/${evidencia.Comprobante_ID}/revision`,
                {
                    method: "PATCH",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        accion,
                        nivelId: Number(form.nivelId),
                        comentario: form.comentario || null,
                        actualizarProgreso:
                            accion === "APROBAR"
                                ? Boolean(form.actualizarProgreso)
                                : false,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "No se pudo revisar la evidencia.");
            }

            if (accion === "APROBAR" && form.finalizarNivel) {
                const usuarioNivelId = nivelSeleccionado?.UsuarioNivel_ID;

                if (!usuarioNivelId) {
                    throw new Error(
                        "La evidencia se aprobó, pero no se encontró el UsuarioNivel_ID para finalizar el nivel."
                    );
                }

                await finalizarNivel({
                    nivelId: Number(form.nivelId),
                    usuarioNivelId,
                    comentario:
                        form.comentario ||
                        `Nivel finalizado por aprobación de evidencia ${evidencia.Comprobante_Folio || evidencia.Comprobante_ID}.`,
                    enviarCorreo: Boolean(form.enviarCorreo),
                    silent: true,
                });
            }

            await fetchAll();

            Swal.fire({
                icon: "success",
                title: "Correcto",
                text:
                    accion === "APROBAR"
                        ? "Evidencia aprobada correctamente."
                        : "Evidencia rechazada correctamente.",
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const finalizarNivel = async ({
        nivelId,
        usuarioNivelId,
        comentario,
        enviarCorreo = true,
        silent = false,
    }) => {
        const nivel = getNivelById(nivelId);

        if (!usuarioNivelId) {
            Swal.fire({
                icon: "warning",
                title: "Nivel inválido",
                text: "No se encontró el identificador del nivel del usuario.",
            });
            return;
        }

        if (nivel?.UsuarioNivel_Estatus === "COMPLETADO") {
            Swal.fire({
                icon: "info",
                title: "Nivel ya completado",
                text: "Este nivel ya fue finalizado anteriormente.",
            });
            return;
        }

        if (!silent) {
            const confirm = await Swal.fire({
                icon: "question",
                title: "¿Finalizar nivel?",
                html: `
                    <p class="mb-1">Se marcará como completado:</p>
                    <strong>${nivel?.Nivel_Nombre || "Nivel seleccionado"}</strong>
                `,
                showCancelButton: true,
                confirmButtonText: "Sí, finalizar",
                cancelButtonText: "Cancelar",
            });

            if (!confirm.isConfirmed) return;
        }

        const response = await fetch(
            `${API_BASE}/api/admin/champions/participantes/${registroId}/niveles/${usuarioNivelId}/finalizar`,
            {
                method: "PATCH",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    comentario: comentario || null,
                    enviarCorreo,
                }),
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(result.message || "No se pudo finalizar el nivel.");
        }

        if (!silent) {
            await fetchAll();

            Swal.fire({
                icon: "success",
                title: "Nivel finalizado",
                text: "El nivel fue finalizado correctamente.",
            });
        }

        return result.data;
    };

    const finalizarNivelManual = async () => {
        if (!accionNivel.nivelId) {
            Swal.fire({
                icon: "warning",
                title: "Selecciona un nivel",
                text: "Debes seleccionar el nivel que deseas finalizar.",
            });
            return;
        }

        const nivel = getNivelById(accionNivel.nivelId);

        try {
            setSaving(true);

            await finalizarNivel({
                nivelId: Number(accionNivel.nivelId),
                usuarioNivelId: nivel?.UsuarioNivel_ID,
                comentario: accionNivel.comentario,
                enviarCorreo: accionNivel.enviarCorreo,
            });

            setAccionNivel({
                nivelId: "",
                comentario: "",
                enviarCorreo: true,
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const finalizarDesafio = async () => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "¿Finalizar desafío completo?",
            text: "Todos los niveles activos del participante se marcarán como completados.",
            showCancelButton: true,
            confirmButtonText: "Sí, finalizar desafío",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);

            const response = await fetch(
                `${API_BASE}/api/admin/champions/participantes/${registroId}/finalizar-desafio`,
                {
                    method: "PATCH",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        comentario:
                            accionNivel.comentario ||
                            "Desafío finalizado desde panel administrativo.",
                        enviarCorreo: accionNivel.enviarCorreo,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "No se pudo finalizar el desafío.");
            }

            await fetchAll();

            Swal.fire({
                icon: "success",
                title: "Desafío finalizado",
                text: "El desafío fue finalizado correctamente.",
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const cambiarEstadoParticipacion = async (tipo) => {
        const isCancel = tipo === "cancelar";

        const confirm = await Swal.fire({
            icon: isCancel ? "warning" : "question",
            title: isCancel ? "¿Cancelar participación?" : "¿Reactivar participación?",
            input: "textarea",
            inputLabel: "Comentario administrativo",
            inputPlaceholder: "Escribe el motivo de esta acción...",
            showCancelButton: true,
            confirmButtonText: isCancel ? "Sí, cancelar" : "Sí, reactivar",
            cancelButtonText: "Cerrar",
        });

        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);

            const response = await fetch(
                `${API_BASE}/api/admin/champions/participantes/${registroId}/${tipo}`,
                {
                    method: "PATCH",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        comentario: confirm.value || null,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "No se pudo actualizar la participación.");
            }

            await fetchAll();

            Swal.fire({
                icon: "success",
                title: "Correcto",
                text: result.message,
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const renderArchivo = (archivo, index) => {
        const url = archivo?.url || archivo?.URL || archivo?.archivo || archivo;
        const nombre =
            archivo?.nombre ||
            archivo?.name ||
            archivo?.filename ||
            `Archivo ${index + 1}`;

        if (!url) return null;

        return (
            <a
                key={`${url}-${index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm me-2 mb-2"
            >
                Descargar {nombre}
            </a>
        );
    };

    const renderEvidenciaCard = (evidencia, canReview = false) => {
        const form = getForm(evidencia.Comprobante_ID);
        const archivos = Array.isArray(evidencia.Comprobante_Archivos_JSON)
            ? evidencia.Comprobante_Archivos_JSON
            : [];

        const nivelSeleccionado = getNivelById(form.nivelId);
        const puedeFinalizarNivel =
            nivelSeleccionado &&
            nivelSeleccionado.UsuarioNivel_Estatus !== "COMPLETADO";

        return (
            <div
                className="card border-0 shadow-sm mb-3"
                key={evidencia.Comprobante_ID}
            >
                <div className="card-header bg-white d-flex flex-wrap justify-content-between gap-2">
                    <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="mb-0">
                                Evidencia #{evidencia.Comprobante_ID}
                            </h6>

                            <span
                                className={`badge ${getEvidenciaBadge(
                                    evidencia.Comprobante_Estatus
                                )}`}
                            >
                                {formatEstatusEvidencia(evidencia.Comprobante_Estatus)}
                            </span>

                            <span className="badge bg-light text-dark border">
                                {evidencia.Comprobante_Origen}
                            </span>

                            <span className="badge bg-light text-dark border">
                                {evidencia.Comprobante_Tipo}
                            </span>
                        </div>

                        <small className="text-muted">
                            Nivel actual de evidencia:{" "}
                            <strong>{evidencia.Nivel_Nombre || "Sin nivel"}</strong>
                        </small>
                    </div>

                    <div className="text-end">
                        <small className="text-muted d-block">
                            Folio: {evidencia.Comprobante_Folio || "Sin folio"}
                        </small>
                        <small className="text-muted d-block">
                            Emisión: {formatDateMexico(evidencia.Comprobante_Fecha_Emision)}
                        </small>
                    </div>
                </div>

                <div className="card-body">
                    <div className="row g-3">
                        <div className={canReview ? "col-xl-8" : "col-12"}>
                            <div className="mb-3">
                                <div className="fw-semibold mb-2">Archivos</div>

                                {archivos.length > 0 ? (
                                    archivos.map(renderArchivo)
                                ) : evidencia.FORV_Archivo_URL ? (
                                    <a
                                        href={evidencia.FORV_Archivo_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-primary btn-sm"
                                    >
                                        Descargar archivo FORV
                                    </a>
                                ) : (
                                    <span className="text-muted">
                                        Sin archivos anexados.
                                    </span>
                                )}
                            </div>

                            {/* {evidencia.forv ? (
                                <div className="alert alert-light border py-2">
                                    <strong>Carga FORV:</strong>{" "}
                                    {evidencia.forv.FORV_Archivo_Nombre || "Archivo FORV"}
                                    <br />
                                    <small className="text-muted">
                                        Filas: {evidencia.forv.FORV_Total_Filas || 0} ·
                                        Válidas: {evidencia.forv.FORV_Filas_Validas || 0} ·
                                        Inválidas:{" "}
                                        {evidencia.forv.FORV_Filas_Invalidas || 0}
                                    </small>
                                </div>
                            ) : null} */}

                            <div className="table-responsive">
                                <table className="table table-sm table-bordered align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Producto / SKU</th>
                                            <th className="text-center">Cantidad</th>
                                            <th className="text-end">Importe</th>
                                            <th className="text-center">Estado</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {evidencia.detalles?.length > 0 ? (
                                            evidencia.detalles.map((detalle) => (
                                                <tr key={detalle.Detalle_ID}>
                                                    <td>
                                                        <div className="fw-semibold">
                                                            {
                                                                detalle.SKU_Codigo_Capturado
                                                            }
                                                        </div>
                                                        <small className="text-muted">
                                                            {
                                                                detalle.Detalle_Producto_Descripcion
                                                            }
                                                        </small>
                                                    </td>

                                                    <td className="text-center">
                                                        {
                                                            detalle.Detalle_Cantidad_Piezas
                                                        }
                                                    </td>

                                                    <td className="text-end">
                                                        $
                                                        {Number(
                                                            detalle.Detalle_Importe_Total ||
                                                                0
                                                        ).toLocaleString("es-MX", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>

                                                    <td className="text-center">
                                                        <span className="badge bg-light text-dark border">
                                                            {
                                                                detalle.Detalle_Estatus
                                                            }
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan="4"
                                                    className="text-center text-muted py-3"
                                                >
                                                    Sin productos registrados.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                    <tfoot>
                                        <tr>
                                            <th>Total</th>
                                            <th className="text-center">
                                                {Number(
                                                    evidencia.totalPiezas || 0
                                                ).toLocaleString("es-MX")}
                                            </th>
                                            <th className="text-end">
                                                $
                                                {Number(
                                                    evidencia.totalImporte || 0
                                                ).toLocaleString("es-MX", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </th>
                                            <th />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {canReview ? (
                            <div className="col-xl-4">
                                <div className="border rounded p-3 bg-light">
                                    <h6 className="mb-3">Revisión administrativa</h6>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            ¿Qué nivel verifica?
                                        </label>
                                        <select
                                            className="form-select"
                                            value={form.nivelId}
                                            onChange={(e) =>
                                                updateForm(
                                                    evidencia.Comprobante_ID,
                                                    "nivelId",
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">Seleccionar nivel</option>
                                            {niveles.map((nivel) => (
                                                <option
                                                    key={nivel.Nivel_ID}
                                                    value={nivel.Nivel_ID}
                                                >
                                                    {nivel.Nivel_Nombre} ·{" "}
                                                    {nivel.UsuarioNivel_Estatus}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            Comentario
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={form.comentario}
                                            onChange={(e) =>
                                                updateForm(
                                                    evidencia.Comprobante_ID,
                                                    "comentario",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Comentario de validación..."
                                        />
                                    </div>

                                    <div className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`progreso-${evidencia.Comprobante_ID}`}
                                            checked={Boolean(form.actualizarProgreso)}
                                            onChange={(e) =>
                                                updateForm(
                                                    evidencia.Comprobante_ID,
                                                    "actualizarProgreso",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`progreso-${evidencia.Comprobante_ID}`}
                                        >
                                            Sumar importe/piezas al progreso
                                        </label>
                                    </div>

                                    <div className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`finalizar-${evidencia.Comprobante_ID}`}
                                            checked={Boolean(form.finalizarNivel)}
                                            disabled={!puedeFinalizarNivel}
                                            onChange={(e) =>
                                                updateForm(
                                                    evidencia.Comprobante_ID,
                                                    "finalizarNivel",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`finalizar-${evidencia.Comprobante_ID}`}
                                        >
                                            Permitir subir de nivel al aprobar
                                        </label>
                                    </div>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`correo-${evidencia.Comprobante_ID}`}
                                            checked={Boolean(form.enviarCorreo)}
                                            disabled={!form.finalizarNivel}
                                            onChange={(e) =>
                                                updateForm(
                                                    evidencia.Comprobante_ID,
                                                    "enviarCorreo",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`correo-${evidencia.Comprobante_ID}`}
                                        >
                                            Enviar correo si sube de nivel
                                        </label>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            disabled={saving}
                                            onClick={() =>
                                                revisarEvidencia(
                                                    evidencia,
                                                    "APROBAR"
                                                )
                                            }
                                        >
                                            Aprobar
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            disabled={saving}
                                            onClick={() =>
                                                revisarEvidencia(
                                                    evidencia,
                                                    "RECHAZAR"
                                                )
                                            }
                                        >
                                            Rechazar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    const renderEmpty = (text) => (
        <div className="text-center py-5 border rounded bg-light">
            <span className="text-muted">{text}</span>
        </div>
    );

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content">
                    <Header />
                    <div className="container-fluid py-4">
                        <div className="text-center py-5">
                            <span className="text-muted">
                                Cargando detalle del participante...
                            </span>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    if (!detalle) {
        return (
            <>
                <Sidebar />
                <main className="main-content">
                    <Header />
                    <div className="container-fluid py-4">
                        <div className="alert alert-warning">
                            No se encontró información del participante.
                        </div>
                    </div>
                </main>
            </>
        );
    }

    const participanteNombre = `${detalle.usuario?.Usuario_Nombre || ""} ${
        detalle.usuario?.Usuario_Apellido || ""
    }`.trim();

    return (
        <>
            <Sidebar />

            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <div className="mb-2">
                                <Link
                                    to="/listado-desafios"
                                    className="btn btn-link px-0 text-decoration-none"
                                >
                                    ← Volver a Champions
                                </Link>
                            </div>

                            <h4 className="mb-1">Detalle del participante</h4>
                            <p className="text-muted mb-0">
                                Revisión de evidencias, progreso y acciones administrativas.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={fetchAll}
                            disabled={saving}
                        >
                            Actualizar
                        </button>
                    </div>

                    <div className="row g-4">
                        <div className="col-xl-3 col-lg-4">
                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-header bg-white">
                                    <h6 className="mb-0">Datos de participación</h6>
                                </div>

                                <div className="card-body">
                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Desafío
                                        </small>
                                        <strong>
                                            {detalle.desafio?.Desafio_Titulo}
                                        </strong>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Participante
                                        </small>
                                        <strong>{participanteNombre}</strong>
                                        <div className="text-muted small">
                                            {detalle.usuario?.Usuario_Correo}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Disty
                                        </small>
                                        <strong>{detalle.disty?.Disty_Nombre}</strong>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Región / Grado
                                        </small>
                                        <strong>
                                            {detalle.region?.Region_Nombre} ·{" "}
                                            {detalle.grado?.Grado_Nombre}
                                        </strong>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Nivel actual
                                        </small>
                                        <strong>
                                            {detalle.nivelActual?.Nivel_Nombre ||
                                                "Sin nivel actual"}
                                        </strong>
                                        <div className="text-muted small">
                                            Estado nivel:{" "}
                                            {detalle.nivelActual
                                                ?.UsuarioNivel_Estatus || "N/A"}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            Estado
                                        </small>
                                        <span
                                            className={`badge p-2  ${getRegistroBadge(
                                                detalle.registro?.Registro_Estatus
                                            )}`}
                                        >
                                            {formatEstatusRegistro(
                                                detalle.registro?.Registro_Estatus
                                            )}
                                        </span>
                                    </div>

                                    <div className="mb-0">
                                        <div className="d-flex justify-content-between mb-1">
                                            <small className="text-muted">
                                                Progreso
                                            </small>
                                            <small className="fw-semibold">
                                                {detalle.progreso
                                                    ?.porcentajeProgreso || 0}
                                                %
                                            </small>
                                        </div>

                                        <div
                                            className="progress"
                                            style={{ height: "8px" }}
                                        >
                                            <div
                                                className="progress-bar"
                                                style={{
                                                    width: `${
                                                        detalle.progreso
                                                            ?.porcentajeProgreso || 0
                                                    }%`,
                                                }}
                                            />
                                        </div>

                                        <small className="text-muted d-block mt-1">
                                            {detalle.progreso
                                                ?.nivelesCompletados || 0}
                                            /
                                            {detalle.progreso?.totalNiveles || 0}{" "}
                                            niveles completados
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white">
                                    <h6 className="mb-0">Acciones rápidas</h6>
                                </div>

                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Pasar a nivel / finalizar nivel
                                        </label>
                                        <select
                                            className="form-select"
                                            value={accionNivel.nivelId}
                                            onChange={(e) =>
                                                setAccionNivel((prev) => ({
                                                    ...prev,
                                                    nivelId: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Seleccionar nivel</option>
                                            {niveles.map((nivel) => (
                                                <option
                                                    key={nivel.Nivel_ID}
                                                    value={nivel.Nivel_ID}
                                                >
                                                    {nivel.Nivel_Nombre} ·{" "}
                                                    {nivel.UsuarioNivel_Estatus}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            Comentario
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={accionNivel.comentario}
                                            onChange={(e) =>
                                                setAccionNivel((prev) => ({
                                                    ...prev,
                                                    comentario: e.target.value,
                                                }))
                                            }
                                            placeholder="Comentario administrativo..."
                                        />
                                    </div>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="enviar-correo-nivel"
                                            checked={accionNivel.enviarCorreo}
                                            onChange={(e) =>
                                                setAccionNivel((prev) => ({
                                                    ...prev,
                                                    enviarCorreo:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="enviar-correo-nivel"
                                        >
                                            Enviar correo
                                        </label>
                                    </div>

                                    <div className="d-grid gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            disabled={saving}
                                            onClick={finalizarNivelManual}
                                        >
                                            Finalizar nivel
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            disabled={saving}
                                            onClick={finalizarDesafio}
                                        >
                                            Finalizar desafío
                                        </button>

                                        {detalle.registro?.Registro_Estatus ===
                                        "CANCELADO" ? (
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm"
                                                disabled={saving}
                                                onClick={() =>
                                                    cambiarEstadoParticipacion(
                                                        "reactivar"
                                                    )
                                                }
                                            >
                                                Reactivar participación
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                disabled={saving}
                                                onClick={() =>
                                                    cambiarEstadoParticipacion(
                                                        "cancelar"
                                                    )
                                                }
                                            >
                                                Cancelar participación
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-9 col-lg-8">
                            {/* <div className="card border-0 shadow-sm mb-4">
                                <div className="card-header bg-white">
                                    <h6 className="mb-0">Progreso por niveles</h6>
                                </div>

                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Nivel</th>
                                                    <th>Meta</th>
                                                    <th>Progreso</th>
                                                    <th>Estado</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {niveles.map((nivel) => (
                                                    <tr key={nivel.Nivel_ID}>
                                                        <td>
                                                            <strong>
                                                                {
                                                                    nivel.Nivel_Nombre
                                                                }
                                                            </strong>
                                                            <div className="text-muted small">
                                                                Orden{" "}
                                                                {
                                                                    nivel.Nivel_Orden
                                                                }
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div>
                                                                Tipo:{" "}
                                                                {
                                                                    nivel.Nivel_Meta_Tipo
                                                                }
                                                            </div>
                                                            <small className="text-muted">
                                                                Importe:{" "}
                                                                {nivel.Nivel_Meta_Importe ||
                                                                    0}{" "}
                                                                · Piezas:{" "}
                                                                {nivel.Nivel_Meta_Piezas ||
                                                                    0}
                                                            </small>
                                                        </td>

                                                        <td>
                                                            <div>
                                                                Importe: $
                                                                {Number(
                                                                    nivel.UsuarioNivel_Progreso_Importe ||
                                                                        0
                                                                ).toLocaleString(
                                                                    "es-MX",
                                                                    {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    }
                                                                )}
                                                            </div>
                                                            <small className="text-muted">
                                                                Piezas:{" "}
                                                                {Number(
                                                                    nivel.UsuarioNivel_Progreso_Piezas ||
                                                                        0
                                                                ).toLocaleString(
                                                                    "es-MX"
                                                                )}
                                                            </small>
                                                        </td>

                                                        <td>
                                                            <span className="badge bg-light text-dark border">
                                                                {
                                                                    nivel.UsuarioNivel_Estatus
                                                                }
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div> */}

                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white">
                                    <h6 className="mb-0">Evidencias</h6>
                                </div>

                                <div className="card-body">
                                    <ul
                                        className="nav nav-tabs mb-3"
                                        role="tablist"
                                    >
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${
                                                    activeTab === "pendientes"
                                                        ? "active"
                                                        : ""
                                                }`}
                                                type="button"
                                                onClick={() =>
                                                    setActiveTab("pendientes")
                                                }
                                            >
                                                Pendientes{" "}
                                                <span className="badge bg-white text-dark ms-1">
                                                    {evidenciasPendientes.length}
                                                </span>
                                            </button>
                                        </li>

                                        {niveles.map((nivel) => (
                                            <li
                                                className="nav-item"
                                                role="presentation"
                                                key={nivel.Nivel_ID}
                                            >
                                                <button
                                                    className={`nav-link ${
                                                        activeTab ===
                                                        `nivel-${nivel.Nivel_ID}`
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                    type="button"
                                                    onClick={() =>
                                                        setActiveTab(
                                                            `nivel-${nivel.Nivel_ID}`
                                                        )
                                                    }
                                                >
                                                    {nivel.Nivel_Nombre}
                                                    <span className="badge bg-success ms-1">
                                                        {evidenciasAprobadasPorNivel[
                                                            nivel.Nivel_ID
                                                        ]?.length || 0}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}

                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${
                                                    activeTab === "rechazados"
                                                        ? "active"
                                                        : ""
                                                }`}
                                                type="button"
                                                onClick={() =>
                                                    setActiveTab("rechazados")
                                                }
                                            >
                                                Rechazados{" "}
                                                <span className="badge bg-danger ms-1">
                                                    {evidenciasRechazadas.length}
                                                </span>
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="tab-content">
                                        <div
                                            className={`tab-pane fade ${
                                                activeTab === "pendientes"
                                                    ? "show active"
                                                    : ""
                                            }`}
                                        >
                                            {evidenciasPendientes.length > 0
                                                ? evidenciasPendientes.map((ev) =>
                                                      renderEvidenciaCard(
                                                          ev,
                                                          true
                                                      )
                                                  )
                                                : renderEmpty(
                                                      "No hay evidencias pendientes por validar."
                                                  )}
                                        </div>

                                        {niveles.map((nivel) => (
                                            <div
                                                key={nivel.Nivel_ID}
                                                className={`tab-pane fade ${
                                                    activeTab ===
                                                    `nivel-${nivel.Nivel_ID}`
                                                        ? "show active"
                                                        : ""
                                                }`}
                                            >
                                                {evidenciasAprobadasPorNivel[
                                                    nivel.Nivel_ID
                                                ]?.length > 0
                                                    ? evidenciasAprobadasPorNivel[
                                                          nivel.Nivel_ID
                                                      ].map((ev) =>
                                                          renderEvidenciaCard(
                                                              ev,
                                                              false
                                                          )
                                                      )
                                                    : renderEmpty(
                                                          `No hay evidencias aprobadas para ${nivel.Nivel_Nombre}.`
                                                      )}
                                            </div>
                                        ))}

                                        <div
                                            className={`tab-pane fade ${
                                                activeTab === "rechazados"
                                                    ? "show active"
                                                    : ""
                                            }`}
                                        >
                                            {evidenciasRechazadas.length > 0
                                                ? evidenciasRechazadas.map((ev) =>
                                                      renderEvidenciaCard(
                                                          ev,
                                                          false
                                                      )
                                                  )
                                                : renderEmpty(
                                                      "No hay evidencias rechazadas."
                                                  )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default ChampionsParticipantes;